import { apiUrl } from "../utils/api";
import { cached, invalidate } from "../utils/swrCache";

export type MailSignatureScope = "user" | "tenant";

/** Mail-Signatur — spiegelt MailSignatureResponse des Backends. */
export interface MailSignature {
    id: number;
    name: string;
    html: string;
    isDefault: boolean;
    /** Postfach, fuer das diese Signatur der Standard ist; "" = allgemein. */
    mailboxAddress: string;
    /**
     * Gehoert die Signatur dem angemeldeten Benutzer? Nur eigene Signaturen
     * werden automatisch aufgeloest — eine mandantenweite Signatur einer
     * Kollegin darf nicht ungefragt in die eigene Mail geraten.
     */
    isOwn: boolean;
    scope: MailSignatureScope;
    createdAt: string;
    updatedAt: string;
}

export interface CreateMailSignatureInput {
    name: string;
    html: string;
    isDefault?: boolean;
    /** Postfach fuer den Standard; "" oder weglassen = allgemein. */
    mailboxAddress?: string;
    scope?: MailSignatureScope;
}

export interface UpdateMailSignatureInput {
    name?: string;
    html?: string;
    isDefault?: boolean;
    mailboxAddress?: string;
    scope?: MailSignatureScope;
}

const fetchJson = async <T>(input: string, init?: RequestInit): Promise<T> => {
    const res = await fetch(input, init);
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        let message = `${res.status} ${res.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) {
                message = Array.isArray(parsed.message)
                    ? parsed.message.join(", ")
                    : String(parsed.message);
            }
        } catch {
            if (text) message = text;
        }
        throw new Error(message);
    }
    const raw = await res.text().catch(() => "");
    return (raw ? JSON.parse(raw) : (undefined as unknown)) as T;
};

const CACHE_KEY = "mail.signatures";

/**
 * Leert den Signatur-Cache. Nach jedem Schreibvorgang aufzurufen, damit ein
 * gerade geoeffnetes Verfassen-Fenster die neue Belegung sieht.
 */
export const invalidateSignatureCache = (): void => invalidate(CACHE_KEY);

export const mailSignatureService = {
    /**
     * `persist: false` ist hier wesentlich: Die Liste enthaelt das vollstaendige
     * `html` inklusive base64-Logos. Der Default `persist: true` wuerde sie in
     * den localStorage schreiben und dort das ~5-MB-Kontingent gefaehrden. Der
     * In-Memory-Cache spart trotzdem den Mehrfach-Fetch pro Fenster.
     */
    async list(): Promise<MailSignature[]> {
        return cached(
            CACHE_KEY,
            () => fetchJson<MailSignature[]>(apiUrl("mail/signatures")),
            { ttlMs: 60_000, persist: false },
        );
    },

    async create(input: CreateMailSignatureInput): Promise<MailSignature> {
        invalidateSignatureCache();
        return fetchJson<MailSignature>(apiUrl("mail/signatures"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
        });
    },

    async update(id: number, patch: UpdateMailSignatureInput): Promise<MailSignature> {
        invalidateSignatureCache();
        return fetchJson<MailSignature>(apiUrl(`mail/signatures/${id}`), {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
        });
    },

    async remove(id: number): Promise<void> {
        invalidateSignatureCache();
        await fetchJson(apiUrl(`mail/signatures/${id}`), { method: "DELETE" });
    },
};
